import * as d3 from 'd3';
import React, { useEffect, useRef } from 'react';

import * as S from './PriceChangeAxes.styles';

export type PriceChangesChartAxesProps = {
  xScale: d3.ScaleLinear<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  yAxisFormatter: (n: number) => string;
  locale?: string;
  isMarketReaction?: boolean;
  dateToIndexMap?: Map<number, number>;
};

export const PriceChangesChartAxes = ({
  xScale,
  yScale,
  yAxisFormatter,
  locale,
  dateToIndexMap,
}: PriceChangesChartAxesProps) => {
  const xAxisRef = useRef<SVGGElement>(null);
  const yAxisRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (xAxisRef.current && xScale.range()[1] > 0) {
      const axisSelection = d3.select(xAxisRef.current);

      const formatterLinearChart = (domainValue: number, idx: number) => {
        if (dateToIndexMap) {
          const time = Array.from(dateToIndexMap.entries()).find(
            ([, index]) => index === domainValue
          )?.[0];

          if (!time) {
            return '';
          }

          const date = new Date(time);
          const minutes = date.getMinutes();

          // Round up to next hour
          if (minutes > 30 && idx !== dateToIndexMap.size - 1) {
            date.setHours(date.getHours() + 1);
          }

          let timeFormatter = d3.timeFormat('%H:00');

          if (minutes === 30) {
            timeFormatter = d3.timeFormat('%H:30');
          }

          const dateFormatter = d3.timeFormat('%b %d');

          const isMarketOpen = date.getHours() === 9;
          if (isMarketOpen) {
            return `${timeFormatter(date)}|${dateFormatter(date)}`;
          } else {
            return timeFormatter(date);
          }
        }

        return '';
      };

      const ticks = () => {
        const [domainStart, domainEnd] = xScale.domain();
        const tickValues: number[] = [];

        if (domainEnd - domainStart < 100) {
          return [domainStart, domainEnd];
        }

        // Split the domain into 6 evenly spaced ticks
        const step = (domainEnd - domainStart) / 5;

        for (let i = 0; i < 6; i++) {
          tickValues.push(Math.floor(domainStart + step * i));
        }

        return tickValues;
      };

      const axis = d3
        .axisBottom<number>(xScale)
        .tickSize(0)
        .tickPadding(16)
        .tickFormat(formatterLinearChart);

      const tickValues = ticks();

      if (Array.isArray(tickValues)) {
        axis.tickValues(tickValues);
      } else {
        axis.ticks(tickValues);
      }

      axisSelection.call(axis);

      // Convert labels with "|" separator to multi-line labels using tspan
      axisSelection.selectAll('.tick text').each(function () {
        const text = d3.select(this);
        const content = text.text();

        if (content.includes('|')) {
          const [timeStr, dateStr] = content.split('|');
          text.text('');
          text.append('tspan').attr('x', 0).attr('dy', '0.71em').text(timeStr);
          text.append('tspan').attr('x', 0).attr('dy', '16px').text(dateStr);
        }
      });
    }

    if (yAxisRef.current && yScale.range()[0] > 0) {
      const axisSelection = d3.select(yAxisRef.current);
      const axis = d3
        .axisRight<number>(yScale)
        .ticks(4)
        .tickSize(xScale.range()[1])
        .tickFormat(yAxisFormatter)
        .tickPadding(10);

      axisSelection.call(axis).call(g => g.select('.domain').remove());
    }
  }, [xScale, yScale, locale, yAxisFormatter]);

  return (
    <g>
      <S.ChartAxisG
        ref={xAxisRef}
        transform={`translate(0, ${yScale.range()[0]})`}
        data-testid="chart-x-axis-group"
      />
      <S.ChartAxisG ref={yAxisRef} />
    </g>
  );
};
